// useAnalytics.js
// Tracker de visitas del sitio. #added 20/9/26 TG
//
// Se monta una sola vez (<AnalyticsTracker /> dentro del Router) y
// manda un pageview cada vez que cambia la ruta.
//
// Regla de oro: si el analytics falla, la página sigue como si nada.
// Por eso todo va dentro de try/catch y nunca tiramos el error. TG

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { phpPath } from "./config";

const VISITOR_KEY = "ef_visitor_id";  // localStorage   -> la misma persona
const SESSION_KEY = "ef_session_id";  // sessionStorage -> una visita
const OWNER_KEY = "ef_owner_key";     // localStorage   -> "esta compu es mía"

// ------------------------------------------------------------
// localStorage puede tronar (modo incógnito, cookies bloqueadas),
// así que todo acceso va envuelto. TG
// ------------------------------------------------------------
const safeGet = (store, key) => {
  try {
    return window[store].getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSet = (store, key, value) => {
  try {
    window[store].setItem(key, value);
  } catch (e) {
    /* ni modo, seguimos sin guardar */
  }
};

const safeRemove = (store, key) => {
  try {
    window[store].removeItem(key);
  } catch (e) {
    /* nada */
  }
};

const makeId = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) {
    /* abajo hay plan B */
  }
  return "x" + Date.now().toString(36) + Math.random().toString(36).slice(2, 11);
};

const getVisitorId = () => {
  let id = safeGet("localStorage", VISITOR_KEY);
  if (!id) {
    id = makeId();
    safeSet("localStorage", VISITOR_KEY, id);
  }
  return id;
};

const getSessionId = () => {
  let id = safeGet("sessionStorage", SESSION_KEY);
  if (!id) {
    id = makeId();
    safeSet("sessionStorage", SESSION_KEY, id);
  }
  return id;
};

// ------------------------------------------------------------
// #Owner
// Entrás una vez a https://eleven.fit/?tg=LA-CLAVE y este navegador
// queda marcado como mío para siempre. Con ?tg=off se quita. TG
// ------------------------------------------------------------
const readOwnerKeyFromUrl = (search) => {
  try {
    const tg = new URLSearchParams(search).get("tg");
    if (tg === null) return;

    if (tg === "off" || tg === "0") {
      safeRemove("localStorage", OWNER_KEY);
    } else if (tg) {
      safeSet("localStorage", OWNER_KEY, tg);
    }
  } catch (e) {
    /* nada */
  }
};

export const isMarkedAsOwner = () => !!safeGet("localStorage", OWNER_KEY);

// ------------------------------------------------------------
// #track
// Exportada aparte para que cualquier componente pueda mandar un
// evento suyo, ej: trackEvent("purchase", { path: "/checkout" }) TG
// ------------------------------------------------------------
export const trackEvent = (eventType, extra = {}) => {
  try {
    const body = JSON.stringify({
      site: "web",
      eventType: eventType || "pageview",
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      ownerKey: safeGet("localStorage", OWNER_KEY) || "",
      path: extra.path || window.location.pathname,
      title: extra.title || document.title || "",
      referrer: extra.referrer !== undefined ? extra.referrer : document.referrer || "",
      language: (navigator.language || "").slice(0, 10),
      screen: `${window.screen.width}x${window.screen.height}`,
      utmSource: extra.utmSource || "",
      utmMedium: extra.utmMedium || "",
      utmCampaign: extra.utmCampaign || "",
    });

    // keepalive para que el envío sobreviva si el usuario se va de
    // la página justo en ese momento. TG
    fetch(`${phpPath}/analytics-track.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* silencio: el analytics nunca molesta al visitante */
    });
  } catch (e) {
    /* silencio */
  }
};

// ------------------------------------------------------------
// #AnalyticsTracker
// Va adentro del <Router>, arriba de las <Routes>. TG
// ------------------------------------------------------------
const AnalyticsTracker = () => {
  const location = useLocation();

  // El referrer real solo sirve en la PRIMERA carga; después de eso
  // el "referrer" sería nuestra propia página anterior. TG
  const firstHit = useRef(true);

  // En StrictMode los effects corren dos veces en dev; este candado
  // evita mandar el pageview duplicado. TG
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname + (location.search || "");
    if (lastPath.current === path) return;
    lastPath.current = path;

    readOwnerKeyFromUrl(location.search);

    let utmSource = "";
    let utmMedium = "";
    let utmCampaign = "";
    try {
      const qs = new URLSearchParams(location.search);
      utmSource = qs.get("utm_source") || "";
      utmMedium = qs.get("utm_medium") || "";
      // #added utm_content (qué video) va pegado a la campaña: "intro|v07". Sin cambiar la tabla 2/11/26 TG
      utmCampaign = [qs.get("utm_campaign"), qs.get("utm_content")].filter(Boolean).join("|");
    } catch (e) {
      /* nada */
    }

    trackEvent("pageview", {
      path: location.pathname,
      referrer: firstHit.current ? document.referrer || "" : "",
      utmSource,
      utmMedium,
      utmCampaign,
    });

    firstHit.current = false;
  }, [location]);

  return null;
};

export default AnalyticsTracker;
